import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { getCachedSiteContent } from "../lib/useSiteContent";
import UpsellModal from "../components/UpsellModal";
import {
  House, Globe, Lock, FolderOpen, ShieldCheck, Lightning, Envelope,
  ChartLineUp, Newspaper, Gear, ArrowRight, ArrowLeft, DownloadSimple, SignOut, ChartBar, Headphones, Sparkle, Star, ArrowUp, ArrowDown, Minus, Storefront
} from "@phosphor-icons/react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import ArticleBlocks from "../components/ArticleBlocks";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, Legend, CartesianGrid, BarChart, Bar, Cell, RadialBarChart, RadialBar, PolarAngleAxis, LabelList, PieChart, Pie, AreaChart, Area } from "recharts";
import { Sheet, SheetTrigger, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { List } from "@phosphor-icons/react";
import ThemeToggle from "../components/ThemeToggle";
import ChartTypeToggle from "../components/ChartTypeToggle";
import LivePreviewModal from "../components/LivePreviewModal";
import ContactPMModal from "../components/ContactPMModal";
import IntelligenceInfographic from "../components/IntelligenceInfographic";
import IntelligenceCharts from "../components/IntelligenceCharts";
import { formatDate } from "../lib/date";

const COLORS = ["#16A34A", "#0A0A0A", "#0055FF", "#F4C430", "#8B5CF6", "#14B8A6"];

function downloadCSV(filename, rows) {
  if (!rows.length) return;
  const keys = Object.keys(rows[0]);
  const csv = [keys.join(","), ...rows.map((r) => keys.map((k) => JSON.stringify(r[k] ?? "")).join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

function NPSGauge({ score }) {
  const value = Math.max(-100, Math.min(100, score));
  const color = value < 0 ? "#dc2626" : value < 30 ? "#f59e0b" : "#16A34A";
  const data = [{ name: "nps", value: value + 100, fill: color }];
  return (
    <div className="relative h-48">
      <ResponsiveContainer>
        <RadialBarChart innerRadius="70%" outerRadius="100%" data={data} startAngle={180} endAngle={0}>
          <PolarAngleAxis type="number" domain={[0, 200]} tick={false} />
          <RadialBar dataKey="value" background={{ fill: "#e5e5e5" }} cornerRadius={4} />
        </RadialBarChart>
      </ResponsiveContainer>
      <div className="absolute inset-0 flex items-center justify-center mt-6">
        <div className="text-center"><div className="font-mono text-4xl font-black" style={{ color }}>{value > 0 ? "+" : ""}{value.toFixed(0)}</div><div className="overline mt-1">NPS</div></div>
      </div>
    </div>
  );
}

function ChartCard({ title, n, badge, children, exportRows, exportName }) {
  return (
    <div className="bg-[#111] p-5 border border-neutral-800">
      <div className="flex items-center justify-between mb-3 gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="overline text-mango">{title}</div>
            {badge}
          </div>
          {n != null && <div className="font-mono text-xs text-neutral-400">N = {n}</div>}
        </div>
        {exportRows && exportRows.length > 0 && (
          <button onClick={() => downloadCSV(`${exportName || "export"}.csv`, exportRows)} className="overline text-xs text-mango hover:underline flex items-center gap-1 shrink-0" data-testid="csv-export">
            <DownloadSimple size={12} /> CSV
          </button>
        )}
      </div>
      {children}
    </div>
  );
}

function TrackerChart({ tracker, n }) {
  const [data, setData] = useState(null);
  const [chartType, setChartType] = useState(tracker.chart_type || "line");
  useEffect(() => { api.get(`/trackers/${tracker.id}/data?min_n=0`).then((r) => setData(r.data)).catch(() => {}); }, [tracker.id]);
  useEffect(() => { setChartType(tracker.chart_type || "line"); }, [tracker.chart_type]);
  if (!data) return <div className="bg-[#111] border border-neutral-800" style={{ minHeight: 200 }} />;
  const lastPoint = data.series[data.series.length - 1] || {};
  // For non-line tracker variants, render the latest point as a single-period chart
  const latestData = data.options.map((o, i) => ({ name: o.text, value: lastPoint[o.key] ?? 0, fill: COLORS[i % COLORS.length] }));

  return (
    <ChartCard title={tracker.name} n={n} exportRows={data.series} exportName={`tracker-${tracker.id}`}>
      <div style={{ minHeight: 240 }}>
        <ResponsiveContainer width="100%" height={Math.max(220, latestData.length * 36 + 40)}>
          {chartType === "line" || chartType === "area" ? (
            (chartType === "area" ? AreaChart : LineChart) === AreaChart ? (
              <AreaChart data={data.series}>
                <CartesianGrid strokeDasharray="3 3" stroke="#262626" vertical={false} />
                <XAxis dataKey="date" stroke="#666" fontSize={11} />
                <YAxis stroke="#666" fontSize={11} unit="%" />
                <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid #16A34A", color: "#fff" }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {data.options.map((o, i) => (
                  <Area key={o.key} type="monotone" name={o.text} dataKey={o.key} stroke={COLORS[i % COLORS.length]} fill={COLORS[i % COLORS.length]} fillOpacity={0.25} strokeWidth={2} />
                ))}
              </AreaChart>
            ) : (
              <LineChart data={data.series}>
                <CartesianGrid strokeDasharray="3 3" stroke="#262626" vertical={false} />
                <XAxis dataKey="date" stroke="#666" fontSize={11} />
                <YAxis stroke="#666" fontSize={11} unit="%" />
                <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid #16A34A", color: "#fff" }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {data.options.map((o, i) => (
                  <Line key={o.key} type="monotone" name={o.text} dataKey={o.key} stroke={COLORS[i % COLORS.length]} strokeWidth={2} dot={{ r: 3 }} />
                ))}
              </LineChart>
            )
          ) : chartType === "pie" || chartType === "donut" ? (
            <PieChart>
              <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid #16A34A", color: "#fff" }} formatter={(v) => `${v}%`} />
              <Legend wrapperStyle={{ fontSize: 11 }} verticalAlign="bottom" height={36} />
              <Pie data={latestData} dataKey="value" nameKey="name" cx="50%" cy="45%" innerRadius={chartType === "donut" ? 50 : 0} outerRadius={75} paddingAngle={2} labelLine={false}
                   label={({ cx, cy, midAngle, innerRadius, outerRadius, value }) => {
                     if (!value || value < 4) return null;
                     const RAD = Math.PI / 180;
                     const r = innerRadius + (outerRadius - innerRadius) * 0.55;
                     const x = cx + r * Math.cos(-midAngle * RAD);
                     const y = cy + r * Math.sin(-midAngle * RAD);
                     return <text x={x} y={y} fill="#fff" textAnchor="middle" dominantBaseline="central" fontSize={11} fontWeight={700}>{`${value}%`}</text>;
                   }}>
                {latestData.map((entry, idx) => <Cell key={idx} fill={entry.fill} />)}
              </Pie>
            </PieChart>
          ) : chartType === "column" ? (
            <BarChart data={latestData} margin={{ left: 10, right: 10, top: 10, bottom: 30 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#262626" horizontal={true} vertical={false} />
              <XAxis dataKey="name" stroke="#a3a3a3" fontSize={11} interval={0} angle={-12} textAnchor="end" height={50} />
              <YAxis stroke="#666" fontSize={11} unit="%" domain={[0, 'dataMax + 10']} />
              <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid #16A34A", color: "#fff" }} formatter={(v) => `${v}%`} />
              <Bar dataKey="value" radius={[2, 2, 0, 0]}>
                {latestData.map((entry, idx) => <Cell key={idx} fill={entry.fill} />)}
                <LabelList dataKey="value" position="top" formatter={(v) => `${v}%`} fill="#fff" fontSize={11} />
              </Bar>
            </BarChart>
          ) : (
            <BarChart data={latestData} layout="vertical" margin={{ left: 10, right: 50 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#262626" horizontal={false} />
              <XAxis type="number" stroke="#666" fontSize={11} unit="%" domain={[0, 'dataMax + 10']} />
              <YAxis type="category" dataKey="name" stroke="#a3a3a3" fontSize={11} width={140} interval={0} />
              <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid #16A34A", color: "#fff" }} formatter={(v) => `${v}%`} />
              <Bar dataKey="value" radius={[0, 2, 2, 0]}>
                {latestData.map((entry, idx) => <Cell key={idx} fill={entry.fill} />)}
                <LabelList dataKey="value" position="right" formatter={(v) => `${v}%`} fill="#fff" fontSize={11} />
              </Bar>
            </BarChart>
          )}
        </ResponsiveContainer>
      </div>
      <ChartTypeToggle kind="tracker" id={tracker.id} current={chartType} onChange={setChartType} />
    </ChartCard>
  );
}

function SnapshotChart({ snap, onOpenDetail }) {
  const data = (snap.options || []).map((o, i) => ({ name: o.text, value: snap.overall?.[o.key] ?? 0, fill: COLORS[i % COLORS.length] }));
  data.sort((a, b) => b.value - a.value);
  const initialType = snap.chart_type || (snap.nps_score != null ? "radial" : "bar");
  const [chartType, setChartType] = useState(initialType);
  useEffect(() => { setChartType(snap.chart_type || (snap.nps_score != null ? "radial" : "bar")); }, [snap.chart_type, snap.nps_score]);

  return (
    <ChartCard title={snap.question_text} n={snap.n_sample} exportRows={data} exportName={`snap-${snap.id}`}>
      {chartType === "radial" || snap.nps_score != null ? <NPSGauge score={snap.nps_score ?? 0} /> : (
        <div style={{ minHeight: Math.max(220, data.length * 36 + 40) }}>
          <ResponsiveContainer width="100%" height={Math.max(220, data.length * 36 + 40)}>
            {chartType === "pie" || chartType === "donut" ? (
              <PieChart>
                <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid #16A34A", color: "#fff" }} formatter={(v) => `${v}%`} />
                <Legend wrapperStyle={{ fontSize: 11 }} verticalAlign="bottom" height={36} />
                <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="45%" innerRadius={chartType === "donut" ? 50 : 0} outerRadius={75} paddingAngle={2} labelLine={false}
                     label={({ cx, cy, midAngle, innerRadius, outerRadius, value }) => {
                       if (!value || value < 4) return null;
                       const RAD = Math.PI / 180;
                       const r = innerRadius + (outerRadius - innerRadius) * 0.55;
                       const x = cx + r * Math.cos(-midAngle * RAD);
                       const y = cy + r * Math.sin(-midAngle * RAD);
                       return <text x={x} y={y} fill="#fff" textAnchor="middle" dominantBaseline="central" fontSize={11} fontWeight={700}>{`${value}%`}</text>;
                     }}>
                  {data.map((entry, idx) => <Cell key={idx} fill={entry.fill} />)}
                </Pie>
              </PieChart>
            ) : chartType === "line" ? (
              <LineChart data={data} margin={{ left: 10, right: 30, top: 10, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#262626" vertical={false} />
                <XAxis dataKey="name" stroke="#a3a3a3" fontSize={11} interval={0} />
                <YAxis stroke="#666" fontSize={11} unit="%" domain={[0, 'dataMax + 10']} />
                <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid #16A34A", color: "#fff" }} formatter={(v) => `${v}%`} />
                <Line type="monotone" dataKey="value" stroke="#16A34A" strokeWidth={3} dot={{ r: 5, fill: "#16A34A" }}>
                  <LabelList dataKey="value" position="top" formatter={(v) => `${v}%`} fill="#fff" fontSize={11} />
                </Line>
              </LineChart>
            ) : chartType === "column" ? (
              <BarChart data={data} margin={{ left: 10, right: 10, top: 10, bottom: 30 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#262626" horizontal={true} vertical={false} />
                <XAxis dataKey="name" stroke="#a3a3a3" fontSize={11} interval={0} angle={-12} textAnchor="end" height={50} />
                <YAxis stroke="#666" fontSize={11} unit="%" domain={[0, 'dataMax + 10']} />
                <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid #16A34A", color: "#fff" }} formatter={(v) => [`${v}%`, "Share"]} />
                <Bar dataKey="value" radius={[2, 2, 0, 0]}>
                  {data.map((entry, idx) => <Cell key={idx} fill={entry.fill} />)}
                  <LabelList dataKey="value" position="top" formatter={(v) => `${v}%`} fill="#fff" fontSize={11} />
                </Bar>
              </BarChart>
            ) : (
              <BarChart data={data} layout="vertical" margin={{ left: 10, right: 50, top: 5, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#262626" horizontal={false} />
                <XAxis type="number" stroke="#666" fontSize={11} unit="%" domain={[0, 'dataMax + 10']} />
                <YAxis type="category" dataKey="name" stroke="#a3a3a3" fontSize={11} width={140} interval={0} />
                <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid #16A34A", color: "#fff" }} formatter={(v) => [`${v}%`, "Share"]} />
                <Bar dataKey="value" radius={[0, 2, 2, 0]}>
                  {data.map((entry, idx) => <Cell key={idx} fill={entry.fill} />)}
                  <LabelList dataKey="value" position="right" formatter={(v) => `${v}%`} fill="#fff" fontSize={11} />
                </Bar>
              </BarChart>
            )}
          </ResponsiveContainer>
        </div>
      )}
      <ChartTypeToggle kind="question" id={snap.question_id || snap.id} current={chartType} onChange={setChartType} />
      {/* ITER 88 — was a <Link to="/snapshots/{id}">, which navigated OUT of
          the enterprise dashboard shell into the public site layout. Now
          opens an in-dashboard detail panel instead (see SnapshotDetailPanel),
          reusing the crosstabs data already loaded in `pub.snapshots`. */}
      <button
        onClick={onOpenDetail}
        className="mt-3 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-mango hover:underline"
        data-testid={`ent-snap-crosstab-link-${snap.id}`}
      >
        Open detail + crosstabs <ArrowRight size={12} />
      </button>
    </ChartCard>
  );
}

export default function EnterpriseDashboard() {
  const { user, loading, logout, loggingOut } = useAuth();
  const nav = useNavigate();
  const [view, setView] = useState("global"); // 'global' | 'private' | 'overview' | 'brand'
  const [data, setData] = useState(() => api.getCached("/enterprise"));
  const [pubData, setPubData] = useState(() => api.getCached("/results/public"));
  const [brandIntel, setBrandIntel] = useState(() => api.getCached("/enterprise/brand-intel"));
  const [upsell, setUpsell] = useState(false);
  const [drawer, setDrawer] = useState(false);

  useEffect(() => {
    if (loggingOut) return;
    if (!loading && !user) { nav("/login", { state: { from: "/enterprise" } }); return; }
    if (!loading && user) {
      api.get("/enterprise").then((r) => setData(r.data)).catch(() => {});
      api.get("/results/public").then((r) => setPubData(r.data)).catch(() => {});
      api.get("/enterprise/brand-intel").then((r) => setBrandIntel(r.data)).catch(() => setBrandIntel({ linked: false }));
    }
  }, [user, loading, loggingOut, nav]);

  if (!data) return <div className="p-12 bg-[#0A0A0A] text-white min-h-screen">Loading vault…</div>;

  const hasGlobal = data.has_global_intelligence_access;
  const hasPrivate = data.has_private_vault_access;
  const hasBrand = !!(brandIntel?.linked && (brandIntel?.enabled_sections || []).length);

  const NavItem = ({ id, icon, label, locked, onChoose }) => (
    <button data-testid={`nav-${id}`} onClick={() => { if (locked) setUpsell(true); else setView(id); onChoose?.(); }}
      className={`w-full flex items-center gap-3 px-4 py-3 text-left text-sm border-l-4 transition-colors ${view === id ? "border-mango bg-[#1a1a1a] text-white" : "border-transparent text-neutral-400 hover:text-white hover:bg-[#151515]"}`}>
      {icon}<span className="flex-1">{label}</span>{locked && <Lock size={12} />}
    </button>
  );

  const onLogout = () => {
    logout();
    nav("/");
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white themable-enterprise">
      <UpsellModal open={upsell} onClose={() => setUpsell(false)} feature="Private Insights vault" />

      {/* Mobile top bar */}
      <header className="md:hidden bg-[#0A0A0A] border-b border-neutral-900 px-4 h-14 flex items-center justify-between sticky top-0 z-40">
        <Link to="/" className="flex items-baseline gap-2"><span className="font-black tracking-tighter text-lg text-white" style={{ fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }}>oodel<span style={{ color: '#16A34A' }}>.</span></span><span className="overline text-neutral-500 text-[0.55rem]">HUB</span></Link>
        <Sheet open={drawer} onOpenChange={setDrawer}>
          <SheetTrigger asChild>
            <button data-testid="enterprise-menu-trigger" aria-label="Open menu" className="w-9 h-9 inline-flex items-center justify-center border border-neutral-700">
              <List size={20} weight="bold" />
            </button>
          </SheetTrigger>
          <SheetContent side="left" className="w-[88vw] max-w-xs p-0 border-0 bg-[#0A0A0A] text-white flex flex-col" data-testid="enterprise-drawer">
            <SheetHeader className="p-5 border-b border-neutral-900 flex-shrink-0">
              <SheetTitle asChild><div><div className="flex items-baseline gap-3"><span className="font-black tracking-tighter leading-none text-lg text-white" style={{ fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }}>oodel<span style={{ color: '#16A34A' }}>.</span></span><span className="overline text-neutral-500">HUB</span></div><div className="overline text-mango mt-1" style={{ fontSize: '0.55rem' }}>ENTERPRISE</div></div></SheetTitle>
            </SheetHeader>
            <div className="flex-1 overflow-y-auto">
              <nav className="py-4">
                <NavItem id="overview" icon={<House size={16} />} label="Overview" onChoose={() => setDrawer(false)} />
                <NavItem id="global" icon={<Globe size={16} />} label="National Intelligence Hub" onChoose={() => setDrawer(false)} />
                <NavItem id="reports" icon={<ChartLineUp size={16} />} label="Reports" onChoose={() => setDrawer(false)} />
                <NavItem id="private" icon={hasPrivate ? <FolderOpen size={16} /> : <Lock size={16} />} label="My Private Insights" locked={!hasPrivate} onChoose={() => setDrawer(false)} />
                {hasBrand && <NavItem id="brand" icon={<Storefront size={16} />} label="Brand Intelligence" onChoose={() => setDrawer(false)} />}
                <NavItem id="editorial" icon={<Newspaper size={16} />} label="Articles" onChoose={() => setDrawer(false)} />
                <NavItem id="account" icon={<Gear size={16} />} label="Account" onChoose={() => setDrawer(false)} />
              </nav>
              <div className="px-5 py-3 text-xs text-neutral-500 border-t border-neutral-900">
                <div className="font-medium">{data.user.name}</div>
                <div className="mt-1 truncate">{data.user.email}</div>
              </div>
            </div>
            <div className="p-5 border-t border-neutral-900 flex-shrink-0 space-y-2">
              <ThemeToggle variant="dark" className="w-full" />
              <button onClick={() => { onLogout(); setDrawer(false); }} className="w-full inline-flex items-center justify-center gap-2 bg-mango hover:bg-mango/90 text-white px-3 py-2 text-sm" data-testid="m-enterprise-logout">
                <SignOut size={14} /> Sign out
              </button>
            </div>
          </SheetContent>
        </Sheet>
      </header>

      <div className="flex">
        {/* Desktop sidebar */}
        <aside className="hidden md:flex w-64 bg-[#0A0A0A] border-r border-neutral-900 sticky top-0 h-screen flex-col flex-shrink-0" data-testid="enterprise-sidebar">
          <Link to="/" className="px-6 py-5 border-b border-neutral-900">
            <div className="flex items-baseline gap-3"><span className="font-black tracking-tighter leading-none text-lg text-white" style={{ fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }}>oodel<span style={{ color: '#16A34A' }}>.</span></span><span className="overline text-neutral-500">HUB</span></div>
            <div className="overline text-mango mt-1" style={{ fontSize: '0.55rem' }}>ENTERPRISE</div>
          </Link>
          <nav className="flex-1 py-4 overflow-y-auto">
            <NavItem id="overview" icon={<House size={16} />} label="Overview" />
            <NavItem id="global" icon={<Globe size={16} />} label="National Intelligence Hub" />
            <NavItem id="reports" icon={<ChartLineUp size={16} />} label="Reports" />
            <NavItem id="private" icon={hasPrivate ? <FolderOpen size={16} /> : <Lock size={16} />} label="My Private Insights" locked={!hasPrivate} />
            {hasBrand && <NavItem id="brand" icon={<Storefront size={16} />} label="Brand Intelligence" />}
            <NavItem id="editorial" icon={<Newspaper size={16} />} label="Articles" />
            <NavItem id="account" icon={<Gear size={16} />} label="Account" />
          </nav>
          <div className="p-4 border-t border-neutral-900 text-xs text-neutral-500 flex-shrink-0">
            <div className="font-medium">{data.user.name}</div>
            <div className="mt-1 truncate">{data.user.email}</div>
            <ThemeToggle variant="dark" className="w-full mt-3" />
            <button onClick={onLogout} data-testid="enterprise-logout" className="mt-2 w-full inline-flex items-center justify-center gap-2 bg-[#1a1a1a] hover:bg-[#262626] border border-neutral-800 px-3 py-2 text-xs text-neutral-300 hover:text-white">
              <SignOut size={12} /> Sign out
            </button>
          </div>
        </aside>

        {/* Main */}
        <main className="flex-1 overflow-x-hidden">
          {view === "overview" && <OverviewView data={data} pub={pubData} hasGlobal={hasGlobal} hasPrivate={hasPrivate} />}
          {view === "global" && <GlobalHubView pub={pubData} hasAccess={hasGlobal} onUnlock={() => setUpsell(true)} />}
          {view === "reports" && <ReportsView />}
          {view === "private" && <PrivateVaultView data={data} hasAccess={hasPrivate} onUnlock={() => setUpsell(true)} />}
          {view === "brand" && <BrandIntelView intel={brandIntel} onRefresh={() => api.get("/enterprise/brand-intel").then((r) => setBrandIntel(r.data)).catch(() => {})} />}
          {view === "editorial" && <EditorialView />}
          {view === "account" && <AccountView data={data} />}
        </main>
      </div>
    </div>
  );
}

function OverviewView({ data, pub, hasGlobal, hasPrivate }) {
  const [siteContent, setSiteContent] = useState(getCachedSiteContent);
  const [showPM, setShowPM] = useState(false);
  useEffect(() => { api.get("/site-content").then((r) => setSiteContent(r.data || {})).catch(() => {}); }, []);
  const tier = (data.user.subscription_tier || "").toLowerCase();
  const isSubscriber = tier === "subscriber";
  const heading = siteContent.subscriber_need_questions_heading || "Need new questions in the next wave?";
  const body = siteContent.subscriber_need_questions_body || "Your dedicated PM ships custom questions into the next omnibus within 48 hours of brief approval.";
  const salesEmail = siteContent.sales_email || "sales@oodel.co";
  return (
    <div className="p-8 space-y-8">
      <div className="border-b border-neutral-800 pb-4">
        <div className="overline text-mango">SECTION 00 · OVERVIEW</div>
        <h1 className="text-4xl font-black tracking-tighter">Welcome, {data.user.name.split(" ")[0]}.</h1>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-neutral-800">
        <AccessTile label="National Intelligence Hub" unlocked={hasGlobal} description="National public trackers + snapshots across the 9 oodel. sectors." count={pub?.trackers?.length ?? 0} suffix="trackers · weighted to be representative" />
        <AccessTile label="My Private Insights" unlocked={hasPrivate} description={hasPrivate ? "Private results scoped to your account." : "Commission custom waves and questions for your team."} count={data.private_trackers.length} suffix="private trackers" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-neutral-800">
        <KPI label="Public Trackers" value={pub?.trackers?.length ?? 0} />
        <KPI label="Public Snapshots" value={pub?.snapshots?.length ?? 0} />
        <KPI label="My Private Trackers" value={data.private_trackers.length} />
        <KPI label="Active Campaigns" value={data.active_campaigns.length} />
      </div>
      <div className="bg-mango text-black p-6 md:p-8" data-testid="overview-direct-line">
        <div className="overline mb-2 flex items-center gap-2"><Lightning size={14} weight="fill" /> DIRECT LINE</div>
        <h3 className="text-3xl font-black tracking-tighter">{heading}</h3>
        <p className="mt-2 text-sm">{body}</p>
        {isSubscriber ? (
          <a href={`mailto:${salesEmail}?subject=Wave%20brief`} className="inline-flex items-center gap-2 mt-4 bg-black text-white px-5 py-3 font-bold" data-testid="ent-pm"><Envelope size={16} /> Contact Sales</a>
        ) : (
          <button onClick={() => setShowPM(true)} className="inline-flex items-center gap-2 mt-4 bg-black text-white px-5 py-3 font-bold hover:bg-neutral-900" data-testid="ent-pm"><Envelope size={16} /> Contact your PM</button>
        )}
      </div>
      <ContactPMModal open={showPM} onClose={() => setShowPM(false)} clientId={data.client_id} />
    </div>
  );
}

function AccessTile({ label, unlocked, description, count, suffix }) {
  return (
    <div className={`p-6 ${unlocked ? "bg-[#111]" : "bg-[#0d0d0d] border border-dashed border-neutral-700"}`}>
      <div className="flex items-center gap-2 mb-2">
        {unlocked ? <FolderOpen size={16} className="text-mango" /> : <Lock size={16} className="text-neutral-500" />}
        <div className="overline text-mango">{label}</div>
        <span className={`text-xs font-mono ml-auto ${unlocked ? "text-mango" : "text-neutral-500"}`}>{unlocked ? "UNLOCKED" : "LOCKED"}</span>
      </div>
      <div className="font-mono text-3xl font-black mt-1">{count}</div>
      <div className="text-xs text-neutral-500 mt-1">{suffix}</div>
      <p className="text-sm text-neutral-300 mt-3">{description}</p>
    </div>
  );
}

function KPI({ label, value }) {
  return <div className="bg-[#111] p-5"><div className="overline text-neutral-500">{label}</div><div className="font-mono text-3xl font-black mt-2">{value}</div></div>;
}

// ITER 88 — in-dashboard replacement for the old `<Link to="/snapshots/{id}">`
// that broke a business customer out of their Enterprise Dashboard shell.
// Renders the exact same overall-distribution + crosstabs breakdown as the
// public Snapshots.jsx detail page, but sourced entirely from data already
// loaded in `pub.snapshots` (no extra API call) and dark-styled to match
// the rest of the dashboard.
function SnapshotDetailPanel({ snap, onBack }) {
  if (!snap) return (
    <div className="p-8">
      <button onClick={onBack} className="overline text-mango hover:underline flex items-center gap-1" data-testid="snapshot-detail-back">
        <ArrowLeft size={12} /> Back to wave
      </button>
    </div>
  );
  const options = snap.options || [];
  return (
    <div className="p-8 space-y-8" data-testid="snapshot-detail-panel">
      <button onClick={onBack} className="overline text-mango hover:underline flex items-center gap-1" data-testid="snapshot-detail-back">
        <ArrowLeft size={12} /> Back to wave
      </button>
      <div className="border-b border-neutral-800 pb-6">
        <div className="overline text-neutral-500 mb-2">SNAPSHOT — {snap.date}</div>
        <h1 className="text-3xl md:text-4xl font-black tracking-tighter" data-testid="snapshot-detail-title">{snap.question_text}</h1>
      </div>
      <div className="bg-[#111] border border-neutral-800 p-6">
        <div className="flex justify-between items-baseline mb-4">
          <div className="overline text-mango">Overall distribution</div>
          {snap.n_sample != null && <div className="font-mono text-xs text-neutral-500">N = {snap.n_sample}</div>}
        </div>
        <div className="space-y-4">
          {options.map((o, i) => {
            const v = snap.overall?.[o.key] ?? 0;
            return (
              <div key={o.key} className="grid grid-cols-12 gap-3 items-center group" title={`${o.text}: ${v}%`}>
                <div className="col-span-3 text-sm font-medium">{o.text}</div>
                <div className="col-span-8 h-7 bg-neutral-900 relative">
                  <div className="h-full transition-all group-hover:opacity-80" style={{ width: `${Math.max(v, 0.5)}%`, background: COLORS[i % COLORS.length] }} />
                </div>
                <div className="col-span-1 text-right font-mono font-semibold">{v}%</div>
              </div>
            );
          })}
        </div>
      </div>
      {Object.entries(snap.crosstabs || {}).map(([cat, groups]) => (
        <div key={cat}>
          <div className="overline text-mango mb-3">Crosstab · {cat}</div>
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-y border-neutral-800">
                <th className="text-left py-3 px-2 font-mono uppercase text-xs text-neutral-500">Group</th>
                {options.map((o) => <th key={o.key} className="text-right py-3 px-2 font-mono uppercase text-xs text-neutral-500">{o.text}</th>)}
              </tr>
            </thead>
            <tbody>
              {Object.entries(groups).map(([g, vals]) => (
                <tr key={g} className="border-b border-neutral-900">
                  <td className="py-3 px-2 font-medium">{g}</td>
                  {options.map((o) => <td key={o.key} className="text-right py-3 px-2 font-mono">{vals[o.key] ?? "—"}{vals[o.key] != null ? "%" : ""}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

function TrackerListCard({ tracker, onClick }) {
  return (
    <button
      onClick={onClick}
      className="text-left bg-[#111] border border-neutral-800 p-5 hover:border-mango transition-colors"
      data-testid={`tracker-list-card-${tracker.id}`}
    >
      <div className="overline text-mango mb-2">{tracker.sector || "GENERAL"} · {tracker.recurrence_cadence || "ad hoc"}</div>
      <h3 className="text-lg font-black tracking-tight leading-snug">{tracker.name}</h3>
      {tracker.latest?.date && <div className="font-mono text-xs text-neutral-500 mt-2">Last updated {formatDate(tracker.latest.date)}</div>}
      <div className="text-xs font-bold uppercase tracking-wider text-neutral-400 mt-4 flex items-center gap-1">
        View chart & detail <ArrowRight size={12} />
      </div>
    </button>
  );
}

function WaveListCard({ wave, onClick }) {
  return (
    <button
      onClick={onClick}
      className="text-left bg-[#111] border border-neutral-800 p-5 hover:border-mango transition-colors"
      data-testid={`wave-list-card-${wave.id}`}
    >
      <div className="overline text-mango mb-2">{wave.date ? formatDate(wave.date) : "UNDATED"}</div>
      <h3 className="text-lg font-black tracking-tight leading-snug">{wave.name}</h3>
      <div className="text-xs font-bold uppercase tracking-wider text-neutral-400 mt-4 flex items-center gap-1">
        {wave.count} snapshot{wave.count !== 1 ? "s" : ""} <ArrowRight size={12} />
      </div>
    </button>
  );
}

function GlobalHubView({ pub, hasAccess, onUnlock }) {
  const [tab, setTab] = useState("trackers"); // 'trackers' | 'waves'
  const [sector, setSector] = useState("all");
  const [period, setPeriod] = useState("all");
  const [sectors, setSectors] = useState([]);
  const [selectedTrackerId, setSelectedTrackerId] = useState(null);
  const [selectedWaveId, setSelectedWaveId] = useState(null);
  const [selectedSnapshotId, setSelectedSnapshotId] = useState(null);
  useEffect(() => { api.get("/sectors").then((r) => setSectors(r.data)).catch(() => {}); }, []);
  if (!pub) return <div className="p-8">Loading…</div>;

  const cutoffMs = periodCutoffMs(period);
  const inPeriod = (iso) => {
    if (!cutoffMs) return true;
    if (!iso) return false;
    const t = new Date(iso).getTime();
    return Number.isFinite(t) && t >= cutoffMs;
  };
  const filterBySector = (item) => sector === "all" || item.sector === sector;
  // A tracker is "in period" if its latest wave was closed inside the window.
  const trackerInPeriod = (t) => period === "all"
    ? true
    : inPeriod(t.latest?.wave_close_at || t.latest?.closed_at || t.latest?.created_at || t.updated_at || t.created_at);
  const snapshotInPeriod = (s) => period === "all"
    ? true
    : inPeriod(s.closed_at || s.created_at || s.updated_at);
  const trackers = pub.trackers.filter(filterBySector).filter(trackerInPeriod);
  const snapshots = pub.snapshots
    .filter((s) => Object.values(s.overall || {}).some((v) => (v || 0) > 0))
    .filter((s) => {
      if (sector === "all") return true;
      // snapshots inherit sector from their question — backend already populates
      return s.sector === sector || s.question?.sector === sector;
    })
    .filter(snapshotInPeriod);

  // ---- deepest level: a single snapshot's full detail + crosstabs ----
  if (selectedSnapshotId) {
    const snap = pub.snapshots.find((s) => s.id === selectedSnapshotId);
    return <SnapshotDetailPanel snap={snap} onBack={() => setSelectedSnapshotId(null)} />;
  }

  // ---- tracker detail (chart + CSV export, reuses TrackerChart as-is) ----
  if (selectedTrackerId) {
    const tracker = pub.trackers.find((t) => t.id === selectedTrackerId);
    return (
      <div className="p-8 space-y-6">
        <button onClick={() => setSelectedTrackerId(null)} className="overline text-mango hover:underline flex items-center gap-1" data-testid="tracker-detail-back">
          <ArrowLeft size={12} /> All trackers
        </button>
        {tracker ? <TrackerChart tracker={tracker} n={tracker.latest?.n_sample} /> : <div className="text-neutral-500">Tracker not found.</div>}
      </div>
    );
  }

  // ---- wave detail: only this wave's snapshots, each with its own crosstab link ----
  if (selectedWaveId) {
    const waveSnaps = snapshots.filter((s) => (s.wave_id || "other") === selectedWaveId);
    const waveName = waveSnaps[0]?.wave_name || "Other results";
    return (
      <div className="p-8 space-y-6">
        <button onClick={() => setSelectedWaveId(null)} className="overline text-mango hover:underline flex items-center gap-1" data-testid="wave-detail-back">
          <ArrowLeft size={12} /> All waves
        </button>
        <div>
          <div className="overline text-neutral-500">WAVE</div>
          <h1 className="text-3xl font-black tracking-tighter" data-testid="wave-detail-title">{waveName}</h1>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {waveSnaps.map((s) => <SnapshotChart key={s.id} snap={s} onOpenDetail={() => setSelectedSnapshotId(s.id)} />)}
        </div>
        {waveSnaps.length === 0 && (
          <div className="text-neutral-500 p-8 text-center border border-dashed border-neutral-800">No snapshots in this wave for the current filters.</div>
        )}
      </div>
    );
  }

  // ---- top level: group snapshots by wave for the "Waves" tab ----
  const waveMap = new Map();
  snapshots.forEach((s) => {
    const key = s.wave_id || "other";
    if (!waveMap.has(key)) waveMap.set(key, { id: key, name: s.wave_name || "Other results", date: s.wave_date || null, count: 0 });
    waveMap.get(key).count += 1;
  });
  const waveGroups = Array.from(waveMap.values()).sort((a, b) => {
    if (!a.date && !b.date) return 0;
    if (!a.date) return 1;
    if (!b.date) return -1;
    return new Date(b.date) - new Date(a.date);
  });

  return (
    <div className="p-8 space-y-6 relative">
      <div className="border-b border-neutral-800 pb-4 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="overline text-mango">SECTION 01 · NATIONAL INTELLIGENCE HUB</div>
          <h1 className="text-4xl font-black tracking-tighter">National trackers & snapshots</h1>
          <p className="text-neutral-400 mt-2 text-sm">Aggregated across the 9 oodel. sectors · weighted to be nationally representative.</p>
        </div>
        <TimeRangeFilter value={period} onChange={setPeriod} />
      </div>

      {/* Trackers / Waves tabs — ITER 88: was one flattened grid mixing both */}
      <div className="flex items-center gap-2" role="tablist" data-testid="global-hub-tabs">
        <button
          onClick={() => setTab("trackers")}
          data-testid="global-hub-tab-trackers"
          className={`px-4 py-2 text-xs font-bold uppercase tracking-wider border-2 transition-colors ${tab === "trackers" ? "border-mango bg-mango/10 text-mango" : "border-neutral-800 text-neutral-400 hover:border-neutral-600"}`}
        >
          Trackers ({trackers.length})
        </button>
        <button
          onClick={() => setTab("waves")}
          data-testid="global-hub-tab-waves"
          className={`px-4 py-2 text-xs font-bold uppercase tracking-wider border-2 transition-colors ${tab === "waves" ? "border-mango bg-mango/10 text-mango" : "border-neutral-800 text-neutral-400 hover:border-neutral-600"}`}
        >
          Waves ({waveGroups.length})
        </button>
      </div>

      {/* Sector filter chips */}
      <div className="flex flex-wrap items-center gap-2 pb-2 border-b border-neutral-800" data-testid="sector-filter">
        <span className="overline text-neutral-500 mr-2">SECTOR</span>
        <button onClick={() => setSector("all")} data-testid="sector-all" className={`px-3 py-1.5 text-xs border-2 ${sector === 'all' ? 'border-mango bg-mango/10 text-mango' : 'border-neutral-800 text-neutral-400 hover:border-neutral-600'}`}>All</button>
        {sectors.map((s) => (
          <button key={s.key} onClick={() => setSector(s.key)} data-testid={`sector-${s.key}`} className={`px-3 py-1.5 text-xs border-2 ${sector === s.key ? 'border-mango bg-mango/10 text-mango' : 'border-neutral-800 text-neutral-400 hover:border-neutral-600'}`}>{s.label}</button>
        ))}
      </div>

      <div className={`relative ${!hasAccess ? "select-none" : ""}`}>
        <div className={!hasAccess ? "filter blur-sm pointer-events-none" : ""}>
          {tab === "trackers" ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="tracker-list-grid">
              {trackers.map((t) => <TrackerListCard key={t.id} tracker={t} onClick={() => setSelectedTrackerId(t.id)} />)}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="wave-list-grid">
              {waveGroups.map((w) => <WaveListCard key={w.id} wave={w} onClick={() => setSelectedWaveId(w.id)} />)}
            </div>
          )}
          {tab === "trackers" && trackers.length === 0 && (
            <div className="text-neutral-500 p-8 text-center border border-dashed border-neutral-800">No trackers in this sector yet.</div>
          )}
          {tab === "waves" && waveGroups.length === 0 && (
            <div className="text-neutral-500 p-8 text-center border border-dashed border-neutral-800">No waves in this sector yet.</div>
          )}
        </div>
        {!hasAccess && (
          <div className="absolute inset-0 flex items-center justify-center" data-testid="global-blurred-overlay">
            <div className="bg-black border-2 border-mango p-8 text-center max-w-md">
              <Lock size={32} weight="fill" className="text-mango mx-auto mb-3" />
              <div className="overline text-mango mb-2">LOCKED</div>
              <h3 className="text-2xl font-black tracking-tighter">Unlock National Intelligence</h3>
              <p className="mt-2 text-sm text-neutral-400">Access all oodel. public trackers and snapshots for strategic insights.</p>
              <button onClick={onUnlock} className="mt-5 inline-flex items-center gap-2 bg-mango text-black px-5 py-3 font-bold" data-testid="global-unlock-cta">
                Contact Sales <ArrowRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------- Time-range filter (Enterprise / Subscriber dashboard) ----------
// Buttons mirror the spec: Today / 7D / 1M / 3M / 6M / 1Y / All.
const PERIOD_OPTIONS = [
  { value: "today", label: "Today", days: 1 },
  { value: "7d", label: "7D", days: 7 },
  { value: "1m", label: "1M", days: 30 },
  { value: "3m", label: "3M", days: 90 },
  { value: "6m", label: "6M", days: 180 },
  { value: "1y", label: "1Y", days: 365 },
  { value: "all", label: "All", days: null },
];

function periodCutoffMs(period) {
  const opt = PERIOD_OPTIONS.find((p) => p.value === period);
  if (!opt || !opt.days) return null;
  return Date.now() - opt.days * 24 * 60 * 60 * 1000;
}

function periodLabel(period) {
  if (period === "all") return "all time";
  if (period === "today") return "today";
  const opt = PERIOD_OPTIONS.find((p) => p.value === period);
  return opt ? `the last ${opt.label}` : "this period";
}

function TimeRangeFilter({ value, onChange }) {
  return (
    <div
      role="group"
      aria-label="Time range"
      className="inline-flex border border-neutral-800 bg-[#111]"
      data-testid="dashboard-time-range"
    >
      {PERIOD_OPTIONS.map((opt) => (
        <button
          key={opt.value}
          onClick={() => onChange(opt.value)}
          data-testid={`time-range-${opt.value}`}
          className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider transition-colors ${
            value === opt.value
              ? "bg-mango text-black font-bold"
              : "text-neutral-400 hover:bg-neutral-900 hover:text-white"
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

function PrivateVaultView({ data, hasAccess, onUnlock }) {
  const [liveWaveId, setLiveWaveId] = useState(null);
  const [period, setPeriod] = useState("all");
  if (!hasAccess) {
    return (
      <div className="p-12">
        <div className="bg-[#111] border-2 border-mango p-12 text-center max-w-2xl mx-auto" data-testid="private-locked">
          <Lock size={36} weight="fill" className="text-mango mx-auto mb-4" />
          <div className="overline text-mango mb-2">PRIVATE VAULT — LOCKED</div>
          <h2 className="text-3xl font-black tracking-tighter">Commission custom research scoped to your team.</h2>
          <p className="mt-3 text-neutral-400">Brief us on a custom audience, brand-tracker, or political wave — we field, weight and surface results here, accessible only to your client_id.</p>
          <button onClick={onUnlock} className="mt-5 inline-flex items-center gap-2 bg-mango text-black px-5 py-3 font-bold" data-testid="private-unlock-cta">
            Contact Sales <ArrowRight size={16} />
          </button>
        </div>
      </div>
    );
  }
  // Time-range filter — buckets all dashboard items by created_at / closed_at /
  // updated_at within the chosen window. "all" disables filtering. Tries the
  // most relevant date on each item and falls back to created_at.
  const cutoffMs = periodCutoffMs(period);
  const inPeriod = (iso) => {
    if (!cutoffMs) return true;
    if (!iso) return false;
    const t = new Date(iso).getTime();
    return Number.isFinite(t) && t >= cutoffMs;
  };
  const allCampaigns = data.active_campaigns || [];
  const visibleActiveCampaigns = period === "all"
    ? allCampaigns
    : allCampaigns.filter((w) => inPeriod(w.created_at || w.opened_at));
  const closedInPeriod = allCampaigns.filter((w) =>
    w.status === "closed" && (period === "all" ? true : inPeriod(w.closed_at || w.updated_at || w.created_at))
  ).length;
  const visiblePrivateTrackers = period === "all"
    ? data.private_trackers
    : data.private_trackers.filter((t) => inPeriod(t.created_at || t.updated_at || t.latest?.wave_close_at));
  const visiblePrivateSnapshots = (period === "all"
    ? data.private_snapshots
    : data.private_snapshots.filter((s) => inPeriod(s.created_at || s.closed_at || s.updated_at))
  ).filter((s) => Object.values(s.overall || {}).some((v) => (v || 0) > 0));
  return (
    <div className="p-8 space-y-10">
      <div className="border-b border-neutral-800 pb-4 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="overline text-mango">SECTION 02 · PRIVATE VAULT</div>
          <h1 className="text-4xl font-black tracking-tighter">Your commissioned research</h1>
        </div>
        <TimeRangeFilter value={period} onChange={setPeriod} />
      </div>

      {data.financials && (
        <div className="bg-[#111] border border-neutral-800 p-5 inline-block">
          <div className="overline text-mango">ACTIVE CAMPAIGNS</div>
          <div className="font-mono text-4xl font-black mt-1">{data.financials.active_campaigns ?? 0}</div>
          <div className="text-xs text-neutral-500 mt-1">{closedInPeriod} closed in {periodLabel(period)}</div>
        </div>
      )}

      <section>
        <div className="overline text-mango mb-3">CAMPAIGNS IN PROGRESS</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          {visibleActiveCampaigns.map((w) => {
            const inField = w.status === "active" || w.status === "paused";
            return (
              <div
                key={w.id}
                className={`bg-[#111] border border-neutral-800 p-5 transition-all ${inField ? "cursor-pointer hover:bg-[#1a1a1a] hover:border-mango" : ""}`}
                onClick={() => inField && setLiveWaveId(w.id)}
                data-testid={`active-campaign-${w.id}`}
                title={inField ? "Click for live fielding preview" : ""}
              >
                <div className="flex items-center justify-between">
                  <div className="overline text-mango">{w.status}</div>
                  {inField && <div className="overline text-neutral-500 text-[10px]">LIVE ↗</div>}
                </div>
                <h3 className="text-lg font-bold mt-1">{w.name}</h3>
                <div className="font-mono text-xs text-neutral-400 mt-2">{w.current_counts?.total || 0} / {w.target_sample}</div>
                <div className="h-1.5 bg-neutral-700 mt-1"><div className="h-full bg-mango" style={{ width: `${Math.min(100, ((w.current_counts?.total || 0) / w.target_sample) * 100)}%` }} /></div>
              </div>
            );
          })}
          {visibleActiveCampaigns.length === 0 && <div className="bg-[#111] border border-neutral-800 p-5 text-neutral-500 col-span-full">No active campaigns in this period.</div>}
        </div>
      </section>

      <section>
        <div className="overline text-mango mb-3">PRIVATE TRACKERS</div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {visiblePrivateTrackers.map((t) => <TrackerChart key={t.id} tracker={t} n={t.latest?.n_sample} />)}
          {visiblePrivateTrackers.length === 0 && <div className="bg-[#111] border border-neutral-800 p-5 text-neutral-500 col-span-full">No private trackers in {periodLabel(period)}.</div>}
        </div>
      </section>

      <section>
        <div className="overline text-mango mb-3">PRIVATE SNAPSHOTS</div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {visiblePrivateSnapshots.map((s) => <SnapshotChart key={s.id} snap={s} />)}
          {visiblePrivateSnapshots.length === 0 && <div className="bg-[#111] border border-neutral-800 p-5 text-neutral-500 col-span-full">No private snapshots in {periodLabel(period)}.</div>}
        </div>
      </section>

      {liveWaveId && <LivePreviewModal waveId={liveWaveId} onClose={() => setLiveWaveId(null)} />}
    </div>
  );
}


function ReportsView() {
  const [items, setItems] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [report, setReport] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => { api.get("/public/reports").then((r) => setItems(r.data)).catch(() => setItems([])); }, []);
  useEffect(() => {
    if (!selectedId) { setReport(null); setErr(null); return; }
    setReport(null); setErr(null);
    api.get(`/public/reports/${selectedId}`)
      .then((r) => setReport(r.data))
      .catch(() => setErr("This report is no longer available."));
  }, [selectedId]);

  // ---------- DETAIL ----------
  if (selectedId) {
    return (
      <div className="p-8">
        <button
          onClick={() => setSelectedId(null)}
          className="inline-flex items-center gap-2 text-sm text-neutral-400 hover:text-white mb-6"
          data-testid="ent-report-back"
        >
          <ArrowLeft size={14} /> All reports
        </button>
        {err && <div className="text-red-400 p-8 border border-dashed border-neutral-800">{err}</div>}
        {!err && !report && <div className="text-neutral-400">Loading report…</div>}
        {report && (
          <div className="border-2 border-black bg-white shadow-2xl text-black" data-testid="ent-report-view">
            <div className="bg-black text-white px-6 py-5 flex items-center justify-between gap-4 border-b-4 border-mango">
              <span className="font-black tracking-tighter text-2xl leading-none whitespace-nowrap">
                <span className="text-white">oodel</span><span style={{ color: "#16A34A" }}>.</span>
              </span>
              <span className="font-black tracking-tighter text-2xl leading-none">
                oodel<sup className="text-sm ml-0.5 align-super" style={{ color: "#16A34A" }}>x</sup>
              </span>
            </div>
            <div className="px-6 py-5 border-b border-neutral-200 bg-gradient-to-b from-neutral-50 to-white">
              <div className="overline text-mango font-bold">{report.report_type_label || "Intelligence Brief"}</div>
              <h1 className="text-2xl sm:text-3xl font-black tracking-tighter mt-1 mb-3">{report.title}</h1>
              <div className="flex items-center gap-2 flex-wrap text-[10px]">
                <span className="px-2 py-1 border border-neutral-300 uppercase tracking-wider bg-white">{report.scope}</span>
                <span className="px-2 py-1 border border-neutral-300 uppercase tracking-wider bg-white">{formatDate(report.created_at)}</span>
              </div>
            </div>
            <div>
              <IntelligenceInfographic scope={report.scope} charts={report.data_payload?.charts} reportTypeLabel={report.report_type_label} />
              <IntelligenceCharts scope={report.scope} charts={report.data_payload?.charts} />
              <div className="p-6 md:p-10 prose prose-sm sm:prose-base max-w-none font-serif leading-relaxed prose-headings:font-black prose-headings:tracking-tighter prose-strong:font-bold prose-strong:text-black prose-p:mb-3 prose-li:my-1 prose-ul:my-3 prose-ul:list-disc prose-ul:pl-6">
                {/* Heading color set via explicit ReactMarkdown component override, not
                    a `prose-h2:` modifier — see matching comment in Admin.jsx for why. */}
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    h2: ({ node, ...props }) => (
                      <h2 className="text-base mt-8 mb-4 px-3 py-2 font-sans font-black tracking-wider uppercase bg-black text-mango border-l-4 border-mango first:mt-0" {...props} />
                    ),
                  }}
                >{report.narrative || ""}</ReactMarkdown>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ---------- LIST ----------
  return (
    <div className="p-8 space-y-6">
      <div className="border-b border-neutral-800 pb-4">
        <div className="overline text-mango">SECTION 03 · REPORTS</div>
        <h1 className="text-4xl font-black tracking-tighter">Deep-dive reports</h1>
        <p className="text-neutral-400 mt-2 text-sm">Analyst-authored briefs built from our tracker and snapshot data — published for your access tier.</p>
      </div>
      {items === null && <div className="text-neutral-400">Loading…</div>}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {(items || []).map((r) => (
          <button
            key={r.id}
            onClick={() => setSelectedId(r.id)}
            className="bg-[#111] p-5 border border-neutral-800 hover:border-mango text-left"
            data-testid={`ent-report-${r.id}`}
          >
            <div className="overline text-mango">{r.report_type_label || r.report_type} · {formatDate(r.created_at)}</div>
            <h3 className="text-lg font-bold mt-2 text-white">{r.title}</h3>
          </button>
        ))}
        {items && items.length === 0 && <div className="md:col-span-2 text-neutral-500 p-6 border border-dashed border-neutral-800">No reports published for your access tier yet.</div>}
      </div>
    </div>
  );
}

function EditorialView() {
  const [items, setItems] = useState([]);
  // selectedId === null → list view; otherwise show detail without leaving the enterprise layout.
  // This is what keeps the sidebar consistent (vs. navigating to /articles/:id which would
  // mount the public site layout and replace the sidebar with the home-page menu).
  const [selectedId, setSelectedId] = useState(null);
  const [article, setArticle] = useState(null);

  useEffect(() => { api.get("/articles").then((r) => setItems(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (!selectedId) { setArticle(null); return; }
    setArticle(null);
    api.get(`/articles/${selectedId}`).then((r) => setArticle(r.data)).catch(() => {});
  }, [selectedId]);

  // ---------- DETAIL ----------
  if (selectedId) {
    if (!article) return <div className="p-8 text-neutral-400">Loading article…</div>;
    return (
      <div className="p-8">
        <button
          onClick={() => setSelectedId(null)}
          className="inline-flex items-center gap-2 text-sm text-neutral-400 hover:text-white mb-6"
          data-testid="article-back"
        >
          <ArrowLeft size={14} /> All articles
        </button>
        <article className="max-w-[820px]">
          <div className="overline mb-3 flex items-center gap-3 text-neutral-400">
            <span className="text-mango">{article.sector?.toUpperCase()}</span>
            <span>·</span>
            <span>{formatDate(article.created_at)}</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-black tracking-tighter leading-[1.05] text-white" data-testid="article-title">{article.title}</h1>
          {article.cover_image && <img src={article.cover_image} alt="" className="w-full aspect-video object-cover mt-8" data-testid="article-cover-image" />}

          {article.audio_url && (
            <div className="mt-8 border border-neutral-800 bg-[#111] p-5">
              <div className="flex items-center gap-2 overline text-mango mb-3"><Headphones size={14} /> DEEP DIVE — LISTEN</div>
              <audio data-testid="article-audio" src={`${process.env.REACT_APP_BACKEND_URL}${article.audio_url}`} controls className="w-full" />
            </div>
          )}

          <div className="mt-10 article-dark">
            <ArticleBlocks blocks={article.blocks} body={article.body} />
          </div>
        </article>
      </div>
    );
  }

  // ---------- LIST ----------
  return (
    <div className="p-8 space-y-6">
      <div className="border-b border-neutral-800 pb-4">
        <div className="overline text-mango">SECTION 06 · ARTICLES</div>
        <h1 className="text-4xl font-black tracking-tighter">Articles &amp; deep dives</h1>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="enterprise-articles-grid">
        {items.map((a) => (
          <button
            key={a.id}
            onClick={() => setSelectedId(a.id)}
            className="bg-[#111] border border-neutral-800 hover:border-mango block text-left transition-colors overflow-hidden"
            data-testid={`enterprise-article-${a.id}`}
          >
            {a.cover_image ? (
              <img src={a.cover_image} alt="" className="w-full aspect-video object-cover" />
            ) : (
              <div className="w-full aspect-video bg-neutral-900 flex items-center justify-center">
                <Newspaper size={28} className="text-neutral-700" />
              </div>
            )}
            <div className="p-5">
              <div className="overline text-mango">{a.sector?.toUpperCase()} · {formatDate(a.created_at)}</div>
              <h3 className="text-lg font-bold mt-2 text-white leading-snug">{a.title}</h3>
            </div>
          </button>
        ))}
        {items.length === 0 && <div className="sm:col-span-2 lg:col-span-3 text-neutral-500 p-6 border border-dashed border-neutral-800">No articles yet.</div>}
      </div>
    </div>
  );
}

function AccountView({ data }) {
  const [showPM, setShowPM] = useState(false);
  const tier = (data.user.subscription_tier || "").toLowerCase();
  const isSubscriber = tier === "subscriber";
  const isEnterprise = tier === "enterprise";
  const [salesEmail, setSalesEmail] = useState("sales@oodel.co");
  useEffect(() => {
    api.get("/site-content").then((r) => {
      const sc = r.data || {};
      if (sc.sales_email) setSalesEmail(sc.sales_email);
    }).catch(() => {});
  }, []);
  return (
    <div className="p-8 space-y-6">
      <div className="border-b border-neutral-800 pb-4">
        <div className="overline text-mango">SECTION 04 · ACCOUNT</div>
        <h1 className="text-4xl font-black tracking-tighter">Your contract</h1>
      </div>
      <div className="bg-[#111] p-6 border border-neutral-800 max-w-2xl space-y-3">
        <Field label="Company" value={data.user.company} />
        <Field label="Client ID" value={data.client_id} />
        <Field label="Contact" value={`${data.user.name} · ${data.user.email}`} />
        <Field label="Subscription Tier" value={(data.user.subscription_tier || "—").toUpperCase()} />
        <Field label="Annual Fee" value={`$${(data.financials?.annual_fee || 0).toLocaleString()}`} />
        <Field label="Per-Survey Fee" value={`$${(data.financials?.per_survey_fee || 0).toLocaleString()}`} />
        <Field label="Payment" value={(data.financials?.payment_status || "pending").toUpperCase()} />
      </div>
      <div className="flex gap-3 flex-wrap">
        {isEnterprise && (
          <button
            onClick={() => setShowPM(true)}
            className="inline-flex items-center gap-2 bg-mango text-black px-5 py-3 font-bold border-2 border-black hover:bg-mango/90"
            data-testid="ent-contact-pm"
          >
            <Envelope size={16} /> Contact your PM
          </button>
        )}
        {isSubscriber && (
          <a
            href={`mailto:${salesEmail}?subject=${encodeURIComponent("Subscriber inquiry · " + (data.client_id || ""))}`}
            className="inline-flex items-center gap-2 bg-mango text-black px-5 py-3 font-bold border-2 border-black hover:bg-mango/90"
            data-testid="sub-contact-sales"
          >
            <Envelope size={16} /> Contact Sales
          </a>
        )}
      </div>
      <ContactPMModal open={showPM} onClose={() => setShowPM(false)} clientId={data.client_id} />
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div className="grid grid-cols-3 gap-3 border-b border-neutral-800 pb-2">
      <div className="overline text-neutral-500">{label}</div>
      <div className="col-span-2 font-mono text-sm">{value || "—"}</div>
    </div>
  );
}


// ============================================================================
// Brand Intelligence — single-page render of the sections the admin enabled
// for THIS customer. Matches enterprise theme (#0A0A0A / mango accents).
// ============================================================================
function BrandIntelView({ intel, onRefresh }) {
  if (intel == null) return <div className="p-8 text-neutral-400">Loading…</div>;
  if (!intel.linked) {
    return (
      <div className="p-12">
        <div className="bg-[#111] border-2 border-mango p-12 text-center max-w-2xl mx-auto">
          <Storefront size={36} weight="fill" className="text-mango mx-auto mb-4" />
          <div className="overline text-mango mb-2">BRAND INTELLIGENCE — NOT LINKED</div>
          <h2 className="text-3xl font-black tracking-tighter">No brand linked to your account.</h2>
          <p className="mt-3 text-neutral-400">Your account manager will link a brand and enable reporting sections shortly.</p>
        </div>
      </div>
    );
  }
  const e = intel.entity;
  const d = intel.data || {};
  const on = (k) => intel.enabled_sections.includes(k);

  return (
    <div className="p-8 space-y-8" data-testid="brand-intel-view">
      <div className="border-b border-neutral-800 pb-4 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="overline text-mango">SECTION 05 · BRAND INTELLIGENCE</div>
          <h1 className="text-4xl font-black tracking-tighter">{e.name}</h1>
          <p className="text-neutral-400 mt-2 text-sm">Sourced from oodel.'s Public Sentiment panel — Pakistanis' verified 1–5★ ratings.</p>
        </div>
        {e.photo_url && <img src={e.photo_url} alt={e.name} className="w-20 h-20 object-cover border border-neutral-800" />}
      </div>

      {on("aggregate_score") && d.aggregate_score && (
        <ChartCard title="AGGREGATE SCORE">
          <div className="flex items-end gap-6 flex-wrap">
            <div>
              <div className="font-mono text-6xl font-black text-white" data-testid="brand-aggregate">{d.aggregate_score.avg_rating ?? "—"}</div>
              <div className="text-xs text-neutral-500 mt-1 font-mono">/ 5 · {d.aggregate_score.total_votes} rating{d.aggregate_score.total_votes === 1 ? "" : "s"}</div>
            </div>
            <BrandTrendBadge delta={d.aggregate_score.trend_delta} />
            <div className="flex-1 min-w-[200px]"><BrandSparkline points={d.aggregate_score.trend_12m} /></div>
          </div>
        </ChartCard>
      )}

      {on("historical_trend") && d.historical_trend && (
        <ChartCard title="HISTORICAL TREND · 24 MONTHS" exportRows={d.historical_trend} exportName={`${e.slug}-trend`}>
          <div style={{ height: 260 }}>
            <ResponsiveContainer>
              <LineChart data={d.historical_trend} margin={{ left: 0, right: 10, top: 10, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#262626" vertical={false} />
                <XAxis dataKey="month" stroke="#666" fontSize={10} interval={2} />
                <YAxis stroke="#666" fontSize={11} domain={[1, 5]} />
                <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid #16A34A", color: "#fff" }} />
                <Line type="monotone" dataKey="avg" stroke="#16A34A" strokeWidth={2.5} dot={{ r: 3 }} connectNulls />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {on("rating_distribution") && d.rating_distribution && (
          <ChartCard title="RATING DISTRIBUTION">
            <BrandHistogram h={d.rating_distribution} />
          </ChartCard>
        )}
        {on("rating_velocity") && d.rating_velocity && (
          <ChartCard title="RECENT VELOCITY">
            <div className="grid grid-cols-2 gap-3">
              <Kv label="Ratings (7d)" value={d.rating_velocity.ratings_7d} />
              <Kv label="Avg (7d)" value={d.rating_velocity.avg_7d ?? "—"} suffix="★" />
              <Kv label="Ratings (30d)" value={d.rating_velocity.ratings_30d} />
              <Kv label="Avg (30d)" value={d.rating_velocity.avg_30d ?? "—"} suffix="★" />
            </div>
          </ChartCard>
        )}

        {on("regional_breakdown") && d.regional_breakdown && (
          <ChartCard title="REGIONAL BREAKDOWN" exportRows={d.regional_breakdown} exportName={`${e.slug}-regional`}>
            <BrandBreakdown rows={d.regional_breakdown} />
          </ChartCard>
        )}
        {on("age_breakdown") && d.age_breakdown && (
          <ChartCard title="AGE BREAKDOWN" exportRows={d.age_breakdown} exportName={`${e.slug}-age`}>
            <BrandBreakdown rows={d.age_breakdown} />
          </ChartCard>
        )}
        {on("gender_breakdown") && d.gender_breakdown && (
          <ChartCard title="GENDER BREAKDOWN" exportRows={d.gender_breakdown} exportName={`${e.slug}-gender`}>
            <BrandBreakdown rows={d.gender_breakdown} />
          </ChartCard>
        )}
        {on("sec_breakdown") && d.sec_breakdown && (
          <ChartCard title="SEC BREAKDOWN" exportRows={d.sec_breakdown} exportName={`${e.slug}-sec`}>
            <BrandBreakdown rows={d.sec_breakdown} />
          </ChartCard>
        )}
        {on("sector_overlap") && d.sector_overlap && (
          <ChartCard title="SECTOR-INTEREST OVERLAP" exportRows={d.sector_overlap} exportName={`${e.slug}-sectors`}>
            {d.sector_overlap.length === 0
              ? <div className="text-sm text-neutral-500">No interest data yet from your raters.</div>
              : <ul className="space-y-1">{d.sector_overlap.map((s) => (
                  <li key={s.sector} className="flex items-center justify-between text-sm">
                    <span className="capitalize">{s.sector.replace(/-/g, " ")}</span>
                    <span className="font-mono text-mango">{s.n}</span>
                  </li>
                ))}</ul>}
          </ChartCard>
        )}
        {on("brand_watchlist") && d.brand_watchlist && (
          <BrandWatchlistCard wl={d.brand_watchlist} onChanged={onRefresh} />
        )}
        {on("brand_events") && Array.isArray(d.brand_events) && (
          <ChartCard title="BRAND EVENTS">
            {d.brand_events.length === 0
              ? <div className="text-sm text-neutral-500">No events logged yet. Your PM will add notable brand events (campaigns, launches, news) here.</div>
              : <ul className="space-y-2">{d.brand_events.map((ev, i) => (
                  <li key={i} className="text-sm border-l-2 border-mango pl-3">
                    <div className="overline text-neutral-500">{formatDate(ev.event_at)}</div>
                    <div className="text-white">{ev.title}</div>
                  </li>
                ))}</ul>}
          </ChartCard>
        )}
        {on("csv_export") && (
          <ChartCard title="EXPORT">
            <p className="text-sm text-neutral-400 mb-3">Download the full sentiment dataset for {e.name}.</p>
            <button
              onClick={() => {
                const rows = (d.historical_trend || []).map((r) => ({ month: r.month, avg: r.avg, n: r.n }));
                downloadCSV(`${e.slug}-full-report.csv`, rows);
              }}
              className="inline-flex items-center gap-2 bg-mango text-black px-4 py-2 font-bold text-sm"
              data-testid="brand-csv-export"
            ><DownloadSimple size={14} /> Download CSV</button>
          </ChartCard>
        )}
      </div>
    </div>
  );
}

function Kv({ label, value, suffix = "" }) {
  return (
    <div className="bg-[#0A0A0A] border border-neutral-800 p-3">
      <div className="overline text-neutral-500 text-[10px]">{label}</div>
      <div className="font-mono text-2xl font-black text-white mt-1">{value}{suffix}</div>
    </div>
  );
}

function BrandTrendBadge({ delta }) {
  if (delta == null) return null;
  const up = delta > 0.05, down = delta < -0.05;
  const Icon = up ? ArrowUp : down ? ArrowDown : Minus;
  const color = up ? "text-emerald-400" : down ? "text-rose-400" : "text-neutral-400";
  const sign = delta > 0 ? "+" : "";
  return (
    <div className={`inline-flex items-center gap-2 ${color}`} data-testid="brand-trend-delta">
      <Icon size={20} weight="bold" />
      <span className="font-mono text-2xl font-black">{sign}{delta.toFixed(2)}</span>
      <span className="text-xs text-neutral-500">12-mo Δ</span>
    </div>
  );
}

function BrandSparkline({ points }) {
  if (!points || points.length === 0) return null;
  const W = 200, H = 50, P = 4;
  const have = points.filter((p) => p.avg != null);
  if (have.length < 1) return null;
  const xs = (i) => P + (i / Math.max(1, points.length - 1)) * (W - 2 * P);
  const ys = (v) => v == null ? null : H - P - ((v - 1) / 4) * (H - 2 * P);
  let d = "", started = false;
  points.forEach((p, i) => {
    const y = ys(p.avg);
    if (y == null) return;
    d += `${started ? "L" : "M"}${xs(i).toFixed(1)} ${y.toFixed(1)} `;
    started = true;
  });
  return <svg viewBox={`0 0 ${W} ${H}`} className="w-full" preserveAspectRatio="none"><path d={d} fill="none" stroke="#16A34A" strokeWidth={2} /></svg>;
}

function BrandHistogram({ h }) {
  const total = Object.values(h).reduce((a, b) => a + (b || 0), 0);
  const pct = (n) => total ? Math.round((n / total) * 1000) / 10 : 0;
  return (
    <div>
      {[5, 4, 3, 2, 1].map((s) => (
        <div key={s} className="flex items-center gap-3 mb-2">
          <div className="text-xs font-mono w-6 text-neutral-400">{s}★</div>
          <div className="flex-1 bg-[#0A0A0A] border border-neutral-900 h-4">
            <div className="bg-mango h-full" style={{ width: `${pct(h[s] || 0)}%` }} />
          </div>
          <div className="text-xs font-mono w-14 text-right text-neutral-400">{pct(h[s] || 0)}%</div>
        </div>
      ))}
      <div className="text-xs text-neutral-500 font-mono mt-2">N = {total}</div>
    </div>
  );
}

function BrandBreakdown({ rows }) {
  if (!rows || rows.length === 0) return <div className="text-sm text-neutral-500">No data yet.</div>;
  const max = Math.max(1, ...rows.map((r) => r.n));
  return (
    <div className="space-y-1.5">
      {rows.map((r) => (
        <div key={r.bucket} className="flex items-center gap-2 text-sm">
          <div className="w-24 truncate text-neutral-300">{r.bucket}</div>
          <div className="flex-1 bg-[#0A0A0A] border border-neutral-900 h-3">
            <div className="bg-mango h-full" style={{ width: `${(r.n / max) * 100}%` }} />
          </div>
          <div className="font-mono text-xs w-20 text-right text-neutral-400">{r.avg}★ · {r.n}</div>
        </div>
      ))}
    </div>
  );
}

// Brand Watchlist card — customer-picked list of brands to track on their
// dashboard. Per product spec shows ONLY: name, avg ★, # raters.
function BrandWatchlistCard({ wl, onChanged }) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState(null);
  const [picked, setPicked] = useState(new Set(wl.selected_ids || []));
  const [saving, setSaving] = useState(false);

  const openModal = async () => {
    setOpen(true);
    setPicked(new Set(wl.selected_ids || []));
    try {
      const r = await api.get("/enterprise/brand-watchlist/options");
      setOptions(r.data?.options || []);
    } catch {
      setOptions([]);
    }
  };

  const toggle = (id) => {
    const next = new Set(picked);
    if (next.has(id)) {
      next.delete(id);
    } else if (next.size < wl.max) {
      next.add(id);
    }
    setPicked(next);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/enterprise/brand-watchlist", { selected_ids: Array.from(picked) });
      setOpen(false);
      onChanged && onChanged();
    } catch (e) {
      // surface the error inline below the modal
      console.error(e);
    } finally { setSaving(false); }
  };

  return (
    <ChartCard title={`BRAND WATCHLIST · ${wl.watched.length} / ${wl.max}`}>
      <div className="flex items-start justify-between mb-3 gap-3">
        <p className="text-xs text-neutral-500">
          Brands you've chosen to track. Updated live from oodel.'s public sentiment panel.
        </p>
        {wl.max > 0 && (
          <button
            onClick={openModal}
            data-testid="brand-watchlist-manage"
            className="bg-mango text-black px-3 py-1.5 text-xs font-bold flex-shrink-0"
          >Manage</button>
        )}
      </div>

      {wl.watched.length === 0 ? (
        <div className="text-sm text-neutral-500" data-testid="brand-watchlist-empty">
          {wl.max > 0 ? "No brands picked yet — click Manage." : "Brand watchlist is not enabled. Contact your account manager."}
        </div>
      ) : (
        <ul className="divide-y divide-neutral-900" data-testid="brand-watchlist-list">
          {wl.watched.map((w) => (
            <li key={w.id} className="py-2 flex items-center gap-3" data-testid={`brand-watchlist-item-${w.slug}`}>
              <div className="flex-1 min-w-0">
                <div className="text-white font-bold truncate">{w.name}</div>
                <div className="overline text-neutral-500 text-[10px]">{w.category_slug}</div>
              </div>
              <div className="text-right">
                <div className="font-mono text-lg font-black text-white">{w.avg_rating ?? "—"}<span className="text-mango">★</span></div>
                <div className="font-mono text-[10px] text-neutral-500">{w.total_votes} rater{w.total_votes === 1 ? "" : "s"}</div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {open && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => !saving && setOpen(false)}
          data-testid="brand-watchlist-modal"
        >
          <div onClick={(e) => e.stopPropagation()} className="bg-[#0A0A0A] border-2 border-mango max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
            <div className="p-5 border-b border-neutral-800 flex items-center justify-between">
              <div>
                <div className="overline text-mango">PICK YOUR BRANDS</div>
                <h3 className="text-2xl font-black tracking-tighter">Brand watchlist · {picked.size} / {wl.max}</h3>
              </div>
              <button onClick={() => setOpen(false)} disabled={saving} className="text-neutral-400 hover:text-white text-2xl leading-none">×</button>
            </div>
            <div className="overflow-y-auto p-3 flex-1">
              {options == null ? (
                <div className="text-neutral-500 p-4">Loading brands…</div>
              ) : options.length === 0 ? (
                <div className="text-neutral-500 p-4">No brands available.</div>
              ) : (
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-1">
                  {options.map((o) => {
                    const isOn = picked.has(o.id);
                    const disabled = !isOn && picked.size >= wl.max;
                    return (
                      <li key={o.id}>
                        <label className={`flex items-center gap-3 p-2 cursor-pointer hover:bg-[#111] ${isOn ? "bg-mango/10" : ""} ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}>
                          <input
                            type="checkbox"
                            checked={isOn}
                            disabled={disabled}
                            onChange={() => toggle(o.id)}
                            data-testid={`watchlist-option-${o.slug}`}
                            className="w-4 h-4"
                          />
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-bold text-white truncate">{o.name}</div>
                            <div className="overline text-neutral-500 text-[9px]">{o.category_slug}</div>
                          </div>
                          <div className="font-mono text-xs text-neutral-400 flex-shrink-0">{o.avg_rating ?? "—"}★ · {o.total_votes || 0}</div>
                        </label>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
            <div className="p-4 border-t border-neutral-800 flex items-center justify-between">
              <div className="text-xs text-neutral-500">{wl.max - picked.size} slot{wl.max - picked.size === 1 ? "" : "s"} left</div>
              <div className="flex gap-2">
                <button onClick={() => setOpen(false)} disabled={saving} className="px-4 py-2 border border-neutral-700 text-neutral-300 text-sm">Cancel</button>
                <button onClick={save} disabled={saving} data-testid="brand-watchlist-save" className="bg-mango text-black px-4 py-2 font-bold text-sm">{saving ? "Saving…" : "Save"}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </ChartCard>
  );
}

